import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import uuid from 'react-native-uuid';

import {
  Attachment,
  Document,
  GetChatQuery,
  Message,
  MessageReaction,
  useCreateMessageIndividualMutation,
  useCreateMessageReactionMutation,
  useDeleteFriendRequestMutation,
  User,
  useRemoveMessageReactionMutation,
} from '@graphql/generated';
import useMe from '@hooks/useMe';

// --- Attachment Classification ---

export const classifyAttachments = (attachments: Attachment[] | undefined) => {
  const isDocumentType = (item: Attachment): item is Document =>
    item.__typename === 'Document';

  const documentAttachments: Document[] = (attachments?.filter((item) => {
    return isDocumentType(item);
  }) ?? []) as Document[];

  const audioAttachments = documentAttachments.filter((item) => item.isAudio);
  const imageAttachments = documentAttachments.filter(
    (item) =>
      item.isImage || item.isVideo || item.contentType?.includes('video')
  );
  const fileAttachments = documentAttachments.filter(
    (item) =>
      !item.isImage &&
      !item.isAudio &&
      !item.isVideo &&
      !item.contentType?.includes('video')
  );

  const taskAssignments = attachments?.filter((item) => {
    return item.__typename === 'MessageTaskAssignment';
  });

  const isAssignTask = taskAssignments && taskAssignments.length > 0;

  const taskCompletes = attachments?.filter((item) => {
    return item.__typename === 'MessageTaskComplete';
  });

  const isCompleteTask = taskCompletes && taskCompletes.length > 0;

  return {
    documentAttachments,
    audioAttachments,
    imageAttachments,
    fileAttachments,
    isAssignTask,
    isCompleteTask,
  };
};

// --- Author Resolution ---

export const resolveAuthor = (
  chatData: GetChatQuery | undefined,
  message: Message
) => {
  const {
    authorId,
    authorName: messageAuthorName,
    authorFirstName: messageAuthorFirstName,
    authorLastName: messageAuthorLastName,
  } = message;

  const response = chatData?.getChat?.users?.filter((item) => {
    return item.id === authorId;
  });

  const authorFirstName =
    (response?.[0] ?? message.author)?.firstName || messageAuthorFirstName;
  const authorLastName =
    (response?.[0] ?? message.author)?.lastName || messageAuthorLastName;
  const nameFallback =
    message.author?.name ?? messageAuthorName ?? 'Unknown';
  const authorName =
    [authorFirstName, authorLastName].filter(Boolean).join(' ') || nameFallback;
  const resolvedAuthor =
    response?.[0] ??
    message.author ??
    ({
      id: authorId,
      name: messageAuthorName || authorName,
    } as User);
  const avatar = (response?.[0] ?? message.author)?.avatar;
  const id = response?.[0]?.id || authorId;

  return {
    response,
    authorFirstName,
    authorLastName,
    authorName,
    resolvedAuthor,
    avatar,
    id,
  };
};

// --- Message Reactions Hook ---

interface UseMessageReactionsOptions {
  messageId: string;
  chatId?: string;
  meId?: string;
  refetchQueries?: any[];
}

export const useMessageReactions = ({
  messageId,
  meId,
  refetchQueries,
}: UseMessageReactionsOptions) => {
  const [removeMessageReactionMutation] = useRemoveMessageReactionMutation(
    refetchQueries ? { refetchQueries } : undefined
  );

  const [createMessageReactionMutation] = useCreateMessageReactionMutation(
    refetchQueries ? { refetchQueries } : undefined
  );

  const updateEmoji = useCallback(
    (xitem: MessageReaction) => {
      if (xitem.user?.some((t: User) => t.id === meId)) {
        removeMessageReactionMutation({
          variables: {
            messageReactionId: xitem.id,
          },
        });
      } else {
        createMessageReactionMutation({
          variables: {
            messageId: messageId,
            reaction: xitem.reaction,
          },
        });
      }
    },
    [
      meId,
      messageId,
      createMessageReactionMutation,
      removeMessageReactionMutation,
    ]
  );

  const _debouncedUpdateEmoji = useMemo(
    () => debounce(updateEmoji, 500),
    [updateEmoji]
  );

  useEffect(() => {
    return () => {
      _debouncedUpdateEmoji.cancel();
    };
  }, [_debouncedUpdateEmoji]);

  return {
    updateEmoji,
    _debouncedUpdateEmoji,
    removeMessageReactionMutation,
    createMessageReactionMutation,
  };
};

// --- Friend Request Logic Hook ---

interface UseFriendRequestLogicOptions {
  chatData?: GetChatQuery;
  onDeclineSuccess: () => void;
  refetchQueries?: {
    createMessage?: any[];
    deleteFriendRequest?: any[];
  };
}

export const useFriendRequestLogic = ({
  chatData,
  onDeclineSuccess,
  refetchQueries,
}: UseFriendRequestLogicOptions) => {
  const { me } = useMe();

  const isReceiverFriendRequest =
    !chatData?.getChat?.friendRequestAccepted &&
    chatData?.getChat?.owner?.id !== me?.id;

  const [showAcceptPopup, setShowAcceptPopUp] = useState(
    isReceiverFriendRequest
  );

  useEffect(() => {
    setShowAcceptPopUp(isReceiverFriendRequest);
  }, [isReceiverFriendRequest]);

  const chatId = chatData?.getChat?.id;

  const [createMessageIndividual] = useCreateMessageIndividualMutation({
    refetchQueries: refetchQueries?.createMessage ?? [
      'listContactUsers',
      'listUsers',
      'getChat',
    ],
  });

  const [deleteFriendRequest] = useDeleteFriendRequestMutation({
    refetchQueries: refetchQueries?.deleteFriendRequest ?? ['listUsers'],
  });

  const onAcceptPress = () => {
    chatId &&
      createMessageIndividual({
        variables: {
          attributes: {
            body: 'Connection request approved',
            chatId: chatId,
            clientId: uuid.v4().toString(),
            projectIds: [],
            replyId: null,
            taskIds: [],
            publish: true,
            checkin: null,
            friendRequestAccepted: true,
          },
        },
      });
  };

  const onDeclinePress = () => {
    chatId &&
      deleteFriendRequest({
        onCompleted: () => {
          onDeclineSuccess();
        },
        variables: {
          friendRequest: true,
          id: chatId,
        },
      });
  };

  return {
    isReceiverFriendRequest,
    showAcceptPopup,
    setShowAcceptPopUp,
    chatId,
    onAcceptPress,
    onDeclinePress,
  };
};
